import { cn } from '@heroui/react';
import { Circle } from 'iconoir-react';

export type EnvironmentType =
  | 'dev'
  | 'test'
  | 'homolog'
  | 'hom'
  | 'preprod'
  | 'prod';

export type EnvironmentIndicatorProps = {
  /** Known env from {@link EnvironmentType}, or any backend string (fallback colours). */
  environment: string;
  className?: string;
};

/** Background classes for env bullets — shared by header, tags, status dots, etc. */
export const ENV_INDICATOR_COLORS: Record<EnvironmentType, string> = {
  dev: 'bg-blue-300',
  test: 'bg-blue-300',
  homolog: 'bg-blue-500',
  hom: 'bg-blue-500',
  preprod: 'bg-blue-700',
  prod: 'bg-pink-500 dark:bg-pink-300'
} as const;

/**
 * Small round status dots (e.g. consumer columns) — reuses {@link ENV_INDICATOR_COLORS} when `active`.
 */
export function getEnvironmentStatusDotClassName(
  environment: string,
  active: boolean
): string {
  if (!active) {
    return 'bg-default-300';
  }
  const key = environment.toLowerCase();
  return (
    (ENV_INDICATOR_COLORS as Record<string, string>)[key] ?? 'bg-default-500'
  );
}

export const EnvironmentIndicator = (props: EnvironmentIndicatorProps) => {
  const { environment, className } = props;

  const colors: Record<string, string> = {
    dev: 'text-blue-300',
    test: 'text-blue-300',
    homolog: 'text-blue-500',
    hom: 'text-blue-500',
    preprod: 'text-blue-700',
    prod: 'text-pink-500 dark:text-pink-300'
  };

  const colorClass = colors[environment] ?? 'text-on-surface-variant';

  return (
    <div className={cn('flex items-center gap-1', colorClass, className)}>
      <Circle role='img' className='h-1.5 w-1.5 fill-current' strokeWidth={0} />
      <span className='font-mono text-label-md tracking-wider text-on-surface-variant'>
        {environment.toUpperCase()}
      </span>
    </div>
  );
};
